import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ErrorButton } from "../../../../../../components/Button/ErrorButton.tsx";
import { Form } from "../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../components/Form/FormContext.ts";
import { Toggle } from "../../../../../../components/Toggle/Toggle.tsx";
import { useAppQuery } from "../../../../../../http/useAppQuery.ts";
import { addNullInput } from "../../../../../../utils/validations.ts";
import { STYLE_MODEL_TYPE } from "../../../../../constants.ts";
import { ImagePicker } from "../../../../components/ImagePicker.tsx";
import { PanelLayout } from "../../../../components/Layout/PanelLayout.tsx";
import { PanelSection } from "../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../components/Panel/Settings/ActionsBar.tsx";
import { ModelSelectionSection } from "../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { PromptSection } from "../../../../components/Panel/Settings/PromptSection.tsx";
import { SliderSubsection } from "../../../../components/Panel/SliderSubsection.tsx";
import { WrappedPanelSection } from "../../../../components/Panel/WrappedPanelSection.tsx";
import { useBoardUuid } from "../../../../hooks/useBoardUuid.ts";
import type { ContentDescription } from "../../../../types.ts";
import { getErrorsFiltered } from "../../../../utils/getErrorsFiltered.ts";
import { usePromptWarnings } from "../../../hooks/usePromptWarnings.ts";
import { scrollCreateWorkspaceGridToTop } from "../../utils/scrollCreateWorkspaceGridToTop.ts";
import {
  initImageColorInfluenceLabelMapping,
  STYLE_TRANSFER_INIT_IMAGE_INFLUENCE,
} from "./constants.ts";
import { useStyleTransferSettings } from "./hooks/useStyleTransferSettings.ts";
import { useTransferStyle } from "./hooks/useTransferStyle.ts";
export const StyleTransferSettings = () => {
  const { mutation: transferStyle, isLoading } = useTransferStyle({
    onSuccess: scrollCreateWorkspaceGridToTop,
  });

  return (
    <Form
      schema={zStyleTransferSettings}
      initialValues={{
        styleTransferBaseImageUuid: "",
        prompt: "",
        selectedModelUuid: null,
        areSelectedModelsValid: false,
      }}
      onSubmit={() => {
        // FIXME: make the mutation async
        transferStyle();
        return Promise.resolve(true);
      }}
    >
      <StyleTransferSettingsFormContent isGenerationLoading={isLoading} />
    </Form>
  );
};

const zStyleTransferSettings = z.object({
  styleTransferBaseImageUuid: z
    .string({ required_error: "Please add an initial image" })
    .uuid(),

  selectedModelUuid: addNullInput(z.string().uuid(), "Please select a model"),
  areSelectedModelsValid: z.boolean().refine((val) => val, {
    message:
      "The selected models are not valid, please try again by reseting the selection.",
  }),
  prompt: z.string().min(1, { message: "Please enter a description" }),
});
type StyleTransferSettingsValues = z.input<typeof zStyleTransferSettings>;

const StyleTransferSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();

  const { tool, settings, setSettings, areSelectedModelsValid, modelActions } =
    useStyleTransferSettings(boardUuid);

  const { data: initImageDescription } = useAppQuery<ContentDescription>({
    queryKey: settings.styleTransferBaseImageUuid
      ? `contents/${settings.styleTransferBaseImageUuid}/description`
      : null,
    enabled: !!settings.styleTransferBaseImageUuid,
  });

  const { setValues, submit, useError } =
    useForm<StyleTransferSettingsValues>();
  const missingModelError = useError((v) => v.selectedModelUuid);
  const modelsNotValidError = useError((v) => v.areSelectedModelsValid);
  const promptEmptyError = useError((v) => v.prompt);
  const initialImageUuidError = useError((v) => v.styleTransferBaseImageUuid);

  useEffect(() => {
    setValues({
      areSelectedModelsValid,
    });
  }, [setValues, areSelectedModelsValid]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  useEffect(() => {
    setValues({
      selectedModelUuid: settings.selectedModels.generationParams.length
        ? settings.selectedModels.generationParams[0].uuid
        : null,
    });
  }, [setValues, settings.selectedModels.generationParams]);

  useEffect(() => {
    setValues({
      styleTransferBaseImageUuid:
        settings.styleTransferBaseImageUuid ?? undefined,
    });
  }, [setValues, settings.styleTransferBaseImageUuid]);

  const previousImageUuid = useRef<string | null>();
  const [isWaitingForDescription, setIsWaitingForDescription] = useState(false);

  useEffect(() => {
    // XXX: the description and its loading state updates differently if the user selects another image.
    // To detect if the user has changed the selected image we use a ref previousImageUuid.
    // Case where the user is still on the same image:
    if (previousImageUuid.current === settings.styleTransferBaseImageUuid) {
      if (isWaitingForDescription && initImageDescription?.description) {
        setSettings({
          prompt: initImageDescription.description,
        });
        setIsWaitingForDescription(false);
      }
    }
    // Case where the user selects another image:
    else {
      previousImageUuid.current = settings.styleTransferBaseImageUuid;
      if (initImageDescription?.description) {
        setIsWaitingForDescription(false);
      } else if (!settings.styleTransferBaseImageUuid) {
        setIsWaitingForDescription(false);
      } else {
        setIsWaitingForDescription(true);
      }
    }
  }, [
    initImageDescription?.description,
    settings.styleTransferBaseImageUuid,
    isWaitingForDescription,

    setSettings,
  ]);

  const warnings = usePromptWarnings({
    prompt: settings.prompt,
    selectedModels: settings.selectedModels.models,
  });

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Generate images"
          intercomArticle="create_a_new_image_from_a_sketch"
          errors={getErrorsFiltered([missingModelError, modelsNotValidError])}
          errorsChildren={
            modelsNotValidError && (
              <ErrorButton
                onClick={() => {
                  modelActions.reset();
                }}
              >
                Reset model selection
              </ErrorButton>
            )
          }
        />
      }
    >
      <PanelSection title="Initial image">
        <ImagePicker
          imageUuid={settings.styleTransferBaseImageUuid ?? undefined}
          onImageUuidChange={(uuid) => {
            setSettings({
              styleTransferBaseImageUuid: uuid ?? null,
            });
          }}
          errors={getErrorsFiltered([initialImageUuidError])}
        />
      </PanelSection>
      <PromptSection
        sectionTitle="Image description"
        prompt={settings.prompt}
        setPrompt={(newPrompt) => {
          setSettings({
            prompt: newPrompt,
          });
        }}
        warnings={warnings}
        placeholder="Image description will be generated after your upload"
        onEnterKeyDown={submit}
        errors={getErrorsFiltered([promptEmptyError])}
      />
      <PanelSection
        title="Input type"
        rightItem={
          <Toggle
            items={STYLE_TRANSFER_INIT_IMAGE_INFLUENCE.map(
              (item) => initImageColorInfluenceLabelMapping[item],
            )}
            index={STYLE_TRANSFER_INIT_IMAGE_INFLUENCE.indexOf(
              settings.init_image_color_influence,
            )}
            onIndexChange={(index) =>
              setSettings({
                init_image_color_influence:
                  STYLE_TRANSFER_INIT_IMAGE_INFLUENCE[index],
              })
            }
          />
        }
      />
      <ModelSelectionSection tool={tool} modelTypes={[STYLE_MODEL_TYPE]} />
      <WrappedPanelSection title="Advanced parameters">
        <div className="flex-col gap-150">
          <SliderSubsection
            min={0}
            max={100}
            value={Math.round(settings.structure_strength * 100)}
            onChange={(value) =>
              setSettings({
                structure_strength: value / 100,
              })
            }
            sliderName="Input structure"
            sliderInformationSection={
              <div className="flex-col gap-400 text-primary body-md-default">
                <span>
                  <span className="body-md-semibold">Input structure</span> -
                  The higher this parameter, the more the structure and shapes
                  of your initial image will be taken into account.
                </span>
              </div>
            }
          />
          <SliderSubsection
            min={0}
            max={100}
            value={Math.round(settings.creativity * 100)}
            onChange={(value) => {
              setSettings({
                creativity: value / 100,
              });
            }}
            sliderName="Creativity"
            sliderInformationSection={
              <div className="flex-col py-100 gap-400 text-primary body-md-default">
                <div>
                  <span className="body-md-semibold">Creativity</span> - The
                  higher this parameter, the more diversity you will have in the
                  generated images.
                </div>
              </div>
            }
          />
          {settings.init_image_color_influence === "COLOR" && (
            <SliderSubsection
              min={0}
              max={100}
              value={Math.round(settings.colors_strength * 100)}
              onChange={(value) =>
                setSettings({
                  colors_strength: value / 100,
                })
              }
              sliderName="Input colors"
              sliderInformationSection={
                <div className="flex-col gap-400 text-primary body-md-default">
                  <div>
                    <span className="body-md-semibold">Input colors</span> - The
                    higher this parameter, the more the colors of your input
                    image will be taken into account. Select 0% if you don't
                    want the colors of your image input to influence your
                    results.
                  </div>
                </div>
              }
            />
          )}
        </div>
      </WrappedPanelSection>
    </PanelLayout>
  );
};
